import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../components/authContext";

import "./PublicListing.css";
import BookNowPublicModal from "../components/BookNowPublicModal";

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

// utils
function cityStateFromAddress(addr = "") {
  const parts = String(addr).split(",").map((s) => s.trim());
  if (parts.length >= 2) return `${parts[0]}, ${parts[1]}`;
  return addr;
}
const dollars = (n) => {
  const x = Number.isFinite(+n) ? +n : 0;
  // no trailing .00 for cleaner UI
  return `$${x.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

export default function PublicListing() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();


  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState(null);
  const [activities, setActivities] = useState([]);
  const [booking, setBooking] = useState(null); // activity object or null

  useEffect(() => {
    if (user && profile) {
      const isOwner = profile.isOwner || (profile.ownerUid && user?.uid && profile.ownerUid === user.uid);
      if (isOwner) {
        navigate("/preview", { replace: true });
      }
    }
  }, [user, profile, navigate]);

  useEffect(() => {
    if (notFound) {
      navigate("/", { replace: true });
    }
  }, [notFound, navigate]);

  // fetch public profile
  useEffect(() => {
    let alive = true;
    async function run() {
      try {
        setLoading(true);
        setNotFound(false);
        setError("");
        const headers = {};
        if (user) {
          const token = await user.getIdToken();
          headers.Authorization = `Bearer ${token}`;
        }
        const r = await fetch(
          `${API_BASE_URL}/api/public/listing/${encodeURIComponent(username)}`,
          { headers });
        if (!r.ok) {
          if (r.status === 404) setNotFound(true);
          else setError("Failed to load page.");
          return;
        }
        const data = await r.json();
        if (!alive) return;
        if (data?.isOwner) {
          navigate("/preview", { replace: true });
          return;
        }
        setProfile(data);

        // activities may be embedded or separate; support both
        if (Array.isArray(data.activities) && data.activities.length) {
          setActivities(data.activities);
        }
      } catch (e) {
        if (alive) setError("Something went wrong loading this page.");
      } finally {
        if (alive) setLoading(false);
      }
    }
    run();
    return () => {
      alive = false;
    };
  }, [username, user, navigate]);

  const name = profile?.displayName || profile?.username || "";
  const avatar = profile?.profilePictureUrl || "";
  const location = useMemo(
    () => cityStateFromAddress(profile?.address),
    [profile]
  );

  if (loading) {
    return (
      <div className="pl-container">
        <div className="pl-card">Loading…</div>
      </div>
    );
  }
  if (notFound) {
    return (
      <div className="pl-container">
        <div className="pl-card">
          <div className="pl-error">This page isn’t available.</div>
          <button className="pl-btn" onClick={() => navigate("/")}>
            Go Home
          </button>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="pl-container">
        <div className="pl-card">
          <div className="pl-error">{error}</div>
          <button className="pl-btn" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pl-container">
      {/* Book modal (UI placeholder) */}
      <BookNowPublicModal
        open={booking}
        onClose={() => setBooking(null)}
        creatorName={name}
        avatarUrl={avatar}
        username={username}
        hostTimeZone={profile?.timeZone}

      />

      <div className="pl-card">
        {/* Header */}
        <div className="pl-head">
          <div className="pl-avatar-wrap">
            {avatar ? (
              <img className="pl-avatar" src={avatar} alt={`${name} avatar`} />
            ) : (
              <div className="pl-avatar pl-avatar-fallback" aria-hidden>
                🙂
              </div>
            )}
          </div>
          <h1 className="pl-name">{name}</h1>
          {location && <div className="pl-location">{location}</div>}
          {profile?.bio && <p className="pl-bio">{profile.bio}</p>}
        </div>

        {/* Activities */}
        <div className="pl-section-title">Activities Offered</div>
        <div className="pl-acts">
          {(activities || []).length === 0 && (
            <div className="pl-act pl-act-empty">No activities yet.</div>
          )}
          {(activities || []).map((a) => (
            <div className="pl-act" key={a.id || a._id || a.title}>
              <div className="pl-act-main">
                <div className="pl-act-title">{a.title}</div>
                <div className="pl-act-sub">
                  {typeof a.price === "number" ? dollars(a.price) : a.price} •{" "}
                  {a.durationLabel || a.duration || "—"}
                </div>
                {a.blurb && <div className="pl-act-blurb">{a.blurb}</div>}
              </div>
              <button
                className="pl-book-btn"
                onClick={() => setBooking(a)}
                aria-label={`Book ${a.title}`}
              >
                Book Now
              </button>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="pl-footer">
          <img src="/logo192.png" alt="ActivityBuddy" className="pl-logo" />
          <div className="pl-powered">
            Powered by <strong>ActivityBuddy</strong>
          </div>
        </div>
      </div>
    </div>
  );
}
